import { captureScopeKeyV2 } from '@/offline/capture/storageV2'
import type { CaptureScopeV2 } from '@/offline/capture/typesV2'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const keyFor = (apiOrigin: string, actorId: string) => `capture-last-prepared-v2:${JSON.stringify([apiOrigin, actorId])}`

export interface LastPreparedCapture {
  scope: CaptureScopeV2
  snapshotId: string
}

/** A single IDs-only navigation hint. The active IndexedDB snapshot remains authoritative. */
export function rememberLastPreparedCapture(scope: CaptureScopeV2, snapshotId: string): boolean {
  try {
    captureScopeKeyV2(scope)
    if (!UUID.test(snapshotId)) return false
    localStorage.setItem(keyFor(scope.apiOrigin, scope.realActorId), JSON.stringify({
      clubId: scope.clubId, seasonId: scope.seasonId, eventId: scope.eventId,
      sessionId: scope.sessionId, snapshotId,
    }))
    return true
  } catch { return false }
}

/** Reads exactly the current actor's pointer; it never scans other accounts or events. */
export function readLastPreparedCapture(actorId: string, apiOrigin: string): LastPreparedCapture | null {
  try {
    if (!UUID.test(actorId) || new URL(apiOrigin).origin !== apiOrigin) return null
    const raw = localStorage.getItem(keyFor(apiOrigin, actorId))
    if (!raw) return null
    const value = JSON.parse(raw) as Record<string, unknown>
    if (!value || typeof value !== 'object' || !UUID.test(String(value.snapshotId || ''))) return null
    const scope: CaptureScopeV2 = {
      apiOrigin, realActorId: actorId, effectiveActorId: actorId,
      clubId: String(value.clubId || ''), seasonId: String(value.seasonId || ''),
      eventId: String(value.eventId || ''), sessionId: String(value.sessionId || ''),
    }
    captureScopeKeyV2(scope)
    return { scope, snapshotId: String(value.snapshotId) }
  } catch { return null }
}

export function lastPreparedCaptureAccessBlocked(scope: CaptureScopeV2): boolean {
  try { return localStorage.getItem(`capture-access-blocked:${JSON.stringify(scope)}`) === '1' }
  catch { return true }
}
